/**
 * Structured addresses (migration 052) — PURE, mirrored in
 * frontend/src/lib/addressParts.ts. Keep the two byte-identical:
 * scripts/address-split-test.ts fails if they drift.
 *
 * Each leg of a lead (origin, destination) has six columns — unit, street,
 * city, province, postal_code, country — and the one-line `<leg>_address`,
 * which is always BUILT from them (formatAddress) whenever they are saved. The
 * line is what every document, email and list reads; the parts are what
 * anything needing a piece of the address reads.
 *
 * The parts map one-to-one onto Google Places address components, for later:
 *   subpremise -> unit, street_number + route -> street, locality -> city,
 *   administrative_area_level_1 (short) -> province, postal_code, country (short).
 *
 * A lead with text but no city is "unsplit" (old data, or a lead whose address
 * could not be read). The page shows the text with a "Check address" hint.
 *
 * splitAddress reads CANADIAN addresses only. Anything else fails, on purpose:
 * a rep enters a non-Canadian address in the fields by hand. When in doubt it
 * fails rather than guesses — a wrong city on a contract is worse than a hint.
 */

export type Leg = 'origin' | 'destination';
export const LEGS: Leg[] = ['origin', 'destination'];

export interface AddressParts {
  unit: string | null;
  street: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  /** 'CA' for Canada; free text otherwise. */
  country: string | null;
}

export const ADDRESS_PART_KEYS = ['unit', 'street', 'city', 'province', 'postal_code', 'country'] as const;

export const PROVINCES: { code: string; name: string }[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const clean = (v: unknown): string | null => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim();
  return s ? s : null;
};

/** "Québec" -> "QUEBEC", "B.C." -> "BC": accents, case and punctuation folded away. */
const fold = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[^A-Z]/g, '');

const PROVINCE_ALIASES: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const p of PROVINCES) { m[p.code] = p.code; m[fold(p.name)] = p.code; }
  const extra: Record<string, string> = {
    PQ: 'QC', QUE: 'QC', ONT: 'ON', ALTA: 'AB', SASK: 'SK', MAN: 'MB', NFLD: 'NL', NEWFOUNDLAND: 'NL',
    PEI: 'PE', NWT: 'NT', YUKONTERRITORY: 'YT',
    COLOMBIEBRITANNIQUE: 'BC', NOUVEAUBRUNSWICK: 'NB', NOUVELLEECOSSE: 'NS',
    TERRENEUVEETLABRADOR: 'NL', ILEDUPRINCEEDOUARD: 'PE', TERRITOIRESDUNORDOUEST: 'NT',
  };
  return { ...m, ...extra };
})();

/** "Ontario", "ont.", "ON" -> "ON". Null when it is not a province. */
export function provinceCode(v: unknown): string | null {
  const s = clean(v);
  return s ? PROVINCE_ALIASES[fold(s)] ?? null : null;
}

/** The first letter of a postal code fixes the province (X is NT or NU, so not used). */
const POSTAL_PROVINCE: Record<string, string> = {
  A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC', K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON',
  R: 'MB', S: 'SK', T: 'AB', V: 'BC', Y: 'YT',
};

export const isCanada = (country: unknown): boolean => {
  const c = clean(country);
  return !c || /^(CA|CAN|CANADA)$/i.test(c);
};

/** "m4y1h1" -> "M4Y 1H1". Anything that is not a Canadian code is kept as typed (uppercased). */
export function normalizePostal(v: unknown): string | null {
  const s = clean(v);
  if (!s) return null;
  const c = s.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(c) ? `${c.slice(0, 3)} ${c.slice(3)}` : s.toUpperCase();
}

/**
 * Tidy parts for saving. Country defaults to Canada ('CA'); for Canada the
 * province becomes its code and the postal code "A1A 1A1". A leg with nothing
 * in it comes back all null, country included.
 */
export function normalizeParts(p: Partial<AddressParts> | null | undefined): AddressParts {
  const unit = clean(p?.unit);
  const street = clean(p?.street);
  const city = clean(p?.city);
  const province = clean(p?.province);
  const postal = clean(p?.postal_code);
  if (!unit && !street && !city && !province && !postal) {
    return { unit: null, street: null, city: null, province: null, postal_code: null, country: null };
  }
  const canada = isCanada(p?.country);
  return {
    unit,
    street,
    city,
    province: canada ? provinceCode(province) ?? (province ? province.toUpperCase() : null) : province,
    postal_code: canada ? normalizePostal(postal) : postal,
    country: canada ? 'CA' : clean(p?.country),
  };
}

// Not "Ste": in Québec that is Sainte ("rue Ste-Catherine"), far more often than Suite.
const UNIT_WORD = '(?:unit|apt\\.?|apartment|suite|app\\.?|appartement|bureau|#)';
/** A unit number has a digit in it ("5", "12B", "B-4"), or is a single letter. */
const UNIT_ID = '([A-Za-z]?\\d[A-Za-z0-9-]*|[A-Za-z])';

/** "5" -> "Unit 5"; "Apt 5", "Suite 200", "#5" are kept as typed. */
export function unitLabel(unit: unknown): string | null {
  const u = clean(unit);
  if (!u) return null;
  return new RegExp(`^${UNIT_WORD}`, 'i').test(u) ? u : `Unit ${u}`;
}

/** The street line with its unit: "Unit 5, 88 Wellesley St E". */
export function streetLine(p: AddressParts): string | null {
  return clean([unitLabel(p.unit), p.street].filter(Boolean).join(', '));
}

/** "Toronto, ON" — never the street. Empty when there is no city. */
export function localityOf(p: AddressParts | null): string {
  if (!p?.city) return '';
  return [p.city, p.province].filter(Boolean).join(', ');
}

/** The one line: "Unit 5, 88 Wellesley St E, Toronto, ON, M4Y 1H1" (country only when not Canada). */
export function formatAddress(p: Partial<AddressParts> | null | undefined): string {
  const n = normalizeParts(p);
  return [unitLabel(n.unit), n.street, n.city, n.province, n.postal_code, n.country !== 'CA' ? n.country : null]
    .filter(Boolean)
    .join(', ');
}

/** The lead's six columns for one leg, as stored. */
export function partsOf(lead: any, leg: Leg): AddressParts {
  const get = (k: string) => clean(lead?.[`${leg}_${k}`]);
  return {
    unit: get('unit'), street: get('street'), city: get('city'),
    province: get('province'), postal_code: get('postal_code'), country: get('country'),
  };
}

/** Everything to write for one leg: the six columns plus the line built from them. */
export function addressColumns(leg: Leg, p: Partial<AddressParts> | null | undefined): Record<string, string | null> {
  const n = normalizeParts(p);
  const out: Record<string, string | null> = {};
  for (const k of ADDRESS_PART_KEYS) out[`${leg}_${k}`] = n[k];
  out[`${leg}_address`] = formatAddress(n) || null;
  return out;
}

/** Text on the lead but no city: show the "Check address" hint. */
export const needsCheck = (lead: any, leg: Leg): boolean =>
  !!clean(lead?.[`${leg}_address`]) && !clean(lead?.[`${leg}_city`]);

export interface SplitResult {
  ok: boolean;
  parts: AddressParts;
  /** Why it failed, for the dry run and the paste box. */
  reason: string | null;
}

/**
 * Split one line of Canadian address into parts. Recognises:
 *   - the postal code anywhere ("M4Y1H1", "m4y 1h1", "M4Y-1H1") -> "M4Y 1H1"
 *   - the province by code or name, English or French, with or without
 *     punctuation ("ON", "Ont.", "Ontario", "Québec", "(Québec)")
 *   - a missing province from the postal code's first letter
 *   - units: "Unit 5", "Apt 5", "Suite 200", "#5", "app. 3", "5-88 Main St",
 *     "88 Main St #5", "88 Main St Apt 5"
 *   - French civic numbers written with a comma: "1234, rue Sainte-Catherine O"
 *   - a trailing "Canada"
 * Fails (ok: false, all parts null) when there is no province, no city, or the
 * city would contain a digit — which is what an address with no commas looks
 * like ("88 Main St Toronto ON"): there is no telling where the street ends.
 * "Toronto, ON" with no street is a success.
 */
export function splitAddress(text: unknown): SplitResult {
  const none: AddressParts = { unit: null, street: null, city: null, province: null, postal_code: null, country: null };
  const fail = (reason: string): SplitResult => ({ ok: false, parts: none, reason });

  let s = clean(text);
  if (!s) return fail('empty');
  if (/\b(USA|U\.S\.A?\.?|United States)\b/i.test(s)) return fail('not a Canadian address');

  // "Montréal (Québec) H2X 1Y4" -> "Montréal, Québec, H2X 1Y4"
  s = s.replace(/\(([^)]*)\)/g, ', $1, ');

  let postal: string | null = null;
  const postals = [...s.matchAll(/\b([A-Za-z]\d[A-Za-z])[ -]?(\d[A-Za-z]\d)\b/g)];
  if (postals.length) {
    const m = postals[postals.length - 1];
    postal = normalizePostal(m[0]);
    s = `${s.slice(0, m.index)},${s.slice((m.index ?? 0) + m[0].length)}`;
  }

  const segs = s.split(',').map((x) => x.replace(/\s+canada$/i, '').trim()).filter(Boolean);
  while (segs.length && fold(segs[segs.length - 1]) === 'CANADA') segs.pop();

  // Province: the whole last segment, or the last words of it ("Toronto ON").
  let province: string | null = null;
  const last = segs.pop();
  if (last) {
    province = provinceCode(last);
    if (!province) {
      const words = last.split(' ');
      for (let k = Math.min(4, words.length - 1); k >= 1 && !province; k--) {
        const code = provinceCode(words.slice(-k).join(' '));
        if (code) { province = code; segs.push(words.slice(0, -k).join(' ')); }
      }
      if (!province) segs.push(last);
    }
  }
  if (!province && postal) province = POSTAL_PROVINCE[postal[0]] ?? null;
  if (!province) return fail('no province');

  const city = segs.pop() ?? null;
  if (!city) return fail('no city');
  if (/\d/.test(city)) return fail('cannot tell the street from the city');

  // "1234, rue X" -> "1234 rue X"
  const rest: string[] = [];
  for (let i = 0; i < segs.length; i++) {
    if (/^\d+[A-Za-z]?$/.test(segs[i]) && segs[i + 1] && !/^\d/.test(segs[i + 1])) {
      rest.push(`${segs[i]} ${segs[i + 1]}`);
      i++;
    } else rest.push(segs[i]);
  }

  let unit: string | null = null;
  const unitOnly = new RegExp(`^${UNIT_WORD}\\s*#?\\s*${UNIT_ID}$`, 'i');
  const unitLead = new RegExp(`^${UNIT_WORD}\\s*#?\\s*${UNIT_ID}\\s*-?\\s+(\\d.*)$`, 'i');
  const unitTrail = new RegExp(`^(\\d.*?)\\s+${UNIT_WORD}\\s*#?\\s*${UNIT_ID}$`, 'i');
  const unitDash = /^([A-Za-z]?\d+[A-Za-z]?)\s*-\s*(\d+[A-Za-z]?\s+\D.*)$/;
  const streets: string[] = [];
  for (const seg of rest) {
    let m: RegExpMatchArray | null;
    if (!unit && (m = seg.match(unitOnly))) { unit = m[1]; continue; }
    if (!unit && (m = seg.match(unitLead))) { unit = m[1]; streets.push(m[2]); continue; }
    if (!unit && (m = seg.match(unitTrail))) { unit = m[2]; streets.push(m[1]); continue; }
    if (!unit && (m = seg.match(unitDash))) { unit = m[1]; streets.push(m[2]); continue; }
    streets.push(seg);
  }

  return {
    ok: true,
    parts: { unit, street: clean(streets.join(', ')), city, province, postal_code: postal, country: 'CA' },
    reason: null,
  };
}

/**
 * The parts to READ for a leg: the columns when the lead has a city, otherwise
 * what the splitter can read from the text, otherwise null. Lets old, unsplit
 * leads still give a city to a merge field or an estimate.
 */
export function resolvedParts(lead: any, leg: Leg): AddressParts | null {
  const stored = partsOf(lead, leg);
  if (stored.city) return normalizeParts(stored);
  const r = splitAddress(lead?.[`${leg}_address`]);
  return r.ok ? r.parts : null;
}
